import moment from "moment";

import { DEAL_UI_STATUS_CODES } from "shared/config/constants";

const APPROVED_STATUSES: string[] = [
  DEAL_UI_STATUS_CODES.approved,
  DEAL_UI_STATUS_CODES.approvedInYardi,
  DEAL_UI_STATUS_CODES.sentToEntrata,
];

const DECLINED_STATUSES: string[] = [
  DEAL_UI_STATUS_CODES.declined,
  DEAL_UI_STATUS_CODES.declinedInYardi,
];

const CANCELLED_STATUSES: string[] = [
  DEAL_UI_STATUS_CODES.cancelled,
  DEAL_UI_STATUS_CODES.cancelledInYardi,
];

export interface ResolvedLeasingDecision {
  label: string;
  /** ISO timestamp of the decision — null for hold states and cancellations. */
  timestamp: string | null;
  /** A verdict was recorded — hold states are false. */
  decided: boolean;
}

export const resolveLeasingDecision = ({
  dealUiStatus,
  approvedAt,
  declinedAt,
  recommendationExists,
}: {
  dealUiStatus?: string | null;
  approvedAt?: string | null;
  declinedAt?: string | null;
  recommendationExists: boolean;
}): ResolvedLeasingDecision => {
  if (dealUiStatus && APPROVED_STATUSES.includes(dealUiStatus)) {
    return { label: "APPROVED", timestamp: approvedAt ?? null, decided: true };
  }
  if (dealUiStatus && DECLINED_STATUSES.includes(dealUiStatus)) {
    return { label: "DECLINED", timestamp: declinedAt ?? null, decided: true };
  }
  if (dealUiStatus && CANCELLED_STATUSES.includes(dealUiStatus)) {
    return { label: "CANCELLED", timestamp: null, decided: true };
  }
  if (dealUiStatus === DEAL_UI_STATUS_CODES.listingClosed) {
    return { label: "LISTING CLOSED", timestamp: null, decided: true };
  }
  return {
    label: recommendationExists ? "Awaiting decision" : "Pending recommendation",
    timestamp: null,
    decided: false,
  };
};

const NON_RECOMMENDATION_SCORES = [
  "N/A",
  "NOT_APPLICABLE",
  "SO_MUCH_EMPTY",
  "STILL_CALCULATING",
];

const PRE_SCREENING_STATUSES: string[] = [
  DEAL_UI_STATUS_CODES.invitesSent,
  DEAL_UI_STATUS_CODES.applicationsIncomplete,
  DEAL_UI_STATUS_CODES.partiallySubmitted,
];

/** A Lumen verdict a leasing agent can act on — placeholders don't count. */
export const lumenRecommendationExists = ({
  lastLumenScore,
  dealUiStatus,
}: {
  lastLumenScore?: string | null;
  dealUiStatus?: string | null;
}): boolean =>
  Boolean(
    lastLumenScore &&
      !NON_RECOMMENDATION_SCORES.includes(lastLumenScore) &&
      (!dealUiStatus || !PRE_SCREENING_STATUSES.includes(dealUiStatus))
  );

/** Review while a recommendation waits on leasing; View everywhere else. */
export const resolveListActionLabel = (
  decision: ResolvedLeasingDecision
): string => (decision.label === "Awaiting decision" ? "Review" : "View");

/** Compact single-line stamp under the decision label, e.g. "Jul 11, 3:02 PM". */
export const formatDecisionTimestamp = (iso: string): string =>
  moment(iso).format("MMM D, h:mm A");
